<script lang="ts" setup>
import { computed, inject, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { useQueryClient } from '@tanstack/vue-query'
import { findGradesByGradeType, findRelevantGradeType } from '~/utils/gradeSorter'
import useArrayUtils from '~/utils/arrayUtils'
import { RadioGroupRoot } from 'reka-ui'
import useSettingsStore from '~/stores/settings'
import { useAuthStore } from '~/stores/auth'
import { PreferredLanguage } from '~/models/User/PreferredLanguage'
import { GradeType } from '~/models/GradeType'
import useSettings from '~/composables/useSettings'
import { SettingsBasic, SettingsGroups, SettingsSubscription } from '.'
import { PendoFeature } from '~/models/Pendo'
import Button from "~/components/ui/Button.vue";
import {
  faArrowUpRightFromSquare,
  faLongArrowRight
} from "@fortawesome/duotone-regular-svg-icons";
import AuDrawer from "~/components/ui/AuDrawer.vue";
import AuRadio from "~/components/ui/AuRadio.vue";
import VgsGradeSelectorPopover from "~/components/settings/VgsGradeSelectorPopover.vue";
import { AuToast } from "~/stores/toasts";
import AuCallout from "~/components/ui/AuCallout.vue";

const {
  toggleSubscriptions,
  toggleGroups,
} = useSettings()

const {
  groupsOpen,
  subscriptionsOpen,
} = storeToRefs(useSettingsStore())

const props = defineProps<{ open: boolean }>()
const emit = defineEmits(['close'])

const { t } = useI18n()
const queryClient = useQueryClient()
const { unique, truthy } = useArrayUtils()
const { setPreferredLanguage, setGradeType, updateUser } = useAuthStore()
const {
  isTeacher,
  hasOrganization,
  userGrades,
  userPreferredLanguage,
  userReadOnlyGrades,
  sessionUserInfo,
  isLoading,
  selectedGradeType
} = storeToRefs(useAuthStore())

let savedPreferredLanguage = ''

const activeGradeType = ref(selectedGradeType.value)
const activePreferredLanguage = ref(userPreferredLanguage.value)

const digitalBooksUrl = computed(() => <string>import.meta.env.VITE_UNIBOK_SITEURL)
const oldContentUrl = computed(() => `${import.meta.env.VITE_AUNIVERS_SITEURL}/fagpakker/nedlastinger/innhold-fra-lokus-lk-06`)

const readOnlyGradesSpansAcrossgradeTypes = computed(() => {
  return userReadOnlyGrades.value.length > 0
    ? userReadOnlyGrades.value.map(findRelevantGradeType).filter(truthy<GradeType>).filter(unique<GradeType>).length > 1
    : false
})

const saveLanguage = () => {
  if (userPreferredLanguage.value as string === savedPreferredLanguage) return

  updateUser(sessionUserInfo.value)
    .then(() => window.location.reload())
    .catch(() =>AuToast.error(t('error')))
}

const saveThenClose = () => {
  if (!userGrades.value.length) {
  AuToast.info(t('settings.chooseGradeToClose'))
    return
  }
  saveLanguage()
  emit('close')
}

const updateGradeType = (gradeType: GradeType) => {
  if (gradeType === selectedGradeType.value) return
  setGradeType(gradeType)
}

watch(props, () => {
  if (props.open) {
    savedPreferredLanguage = userPreferredLanguage.value
  }
})

watch(userGrades, () => {
  if (!isLoading.value && props.open) {
    return queryClient.invalidateQueries({
      predicate: (query) => !query.queryKey.includes('dont-invalidate-on-grade-change')
    })
  }
})
</script>

<template>
  <AuDrawer
    :open="props.open"
    :title="t('settings.title')"
    @close="saveThenClose"
  >
    <template #content>
      <div class="flex flex-col gap-8 pb-16">
        <SettingsBasic/>
        <AuCallout
          v-if="readOnlyGradesSpansAcrossgradeTypes"
          :title="t('settings.readOnlyGradesSpansAcrossgradeTypes.title')"
          variant="info"
        >
          <p v-text="t('settings.readOnlyGradesSpansAcrossgradeTypes.info')" />
        </AuCallout>
        <fieldset>
          <legend
            class="mb-2 text-base font-medium uppercase tracking-wide"
            v-text="t('settings.sections.gradeType')"
          />
          <RadioGroupRoot
            v-model="activeGradeType"
            class="grid grid-cols-2 gap-4"
            @update:model-value="(val) => updateGradeType(val as GradeType)"
          >
            <Button
              v-for="gradeType in GradeType"
              :key="gradeType"
              :disabled="isLoading"
              size="lg"
              type="label"
            >
              <AuRadio :id="gradeType" :value="gradeType" shade="light" />
              {{ t(`metadata.gradeTypes.${gradeType}`) }}
              <VgsGradeSelectorPopover v-model="activeGradeType" v-if="gradeType === GradeType.GradeVgs" class="ml-auto" />
            </Button>
          </RadioGroupRoot>
        </fieldset>
        <fieldset class="flex gap-3">
          <legend
            class="mb-2 text-base font-medium uppercase tracking-wide"
            v-text="t('settings.sections.language')"
          />
          <RadioGroupRoot v-model="activePreferredLanguage" class="flex items-center gap-3" @update:model-value="val => setPreferredLanguage(val as PreferredLanguage)">
            <Button
              :data-pendo="PendoFeature.Settings.ChangeLanguage"
              size="lg"
              type="label"
            >
              <AuRadio :id="PreferredLanguage.Bokmal" :value="PreferredLanguage.Bokmal" shade="light" />
              {{ t(`metadata.language.${PreferredLanguage.Bokmal}`) }}
            </Button>
            <Button
              :data-pendo="PendoFeature.Settings.ChangeLanguage"
              size="lg"
              type="label"
            >
              <AuRadio :id="PreferredLanguage.Nynorsk" :value="PreferredLanguage.Nynorsk" shade="light" />
              {{ t(`metadata.language.${PreferredLanguage.Nynorsk}`) }}
            </Button>
          </RadioGroupRoot>
        </fieldset>
        <div v-if="isTeacher">
          <p
            class="mb-2 text-base font-medium uppercase tracking-wide"
            v-text="t('settings.sections.instruction')"
          />
          <div class="flex flex-col gap-3">
            <Button
              :data-pendo="PendoFeature.Settings.Groups"
              :disabled="!hasOrganization"
              class="justify-between"
              size="lg"
              @click="toggleGroups"
            >
              {{ t('settings.drawers.groups') }}
              <font-awesome-icon :icon="faLongArrowRight"  />
            </Button>
            <Button
              :data-pendo="PendoFeature.Settings.Subscriptions"
              class="justify-between"
              size="lg"
              @click="toggleSubscriptions"
            >
              {{ t('settings.drawers.subscription') }}
              <font-awesome-icon :icon="faLongArrowRight"/>
            </Button>
          </div>
        </div>
        <div>
          <p
            class="mb-2 text-base font-medium uppercase tracking-wide"
            v-text="t('settings.drawers.shortcuts')"
          />
          <div class="flex flex-col gap-3">
            <Button
              :data-pendo="PendoFeature.Settings.ShortcutUnibok"
              :href="digitalBooksUrl"
              class="justify-between"
              size="lg"
            >
              {{ t('settings.shortcuts.digitalBooks') }}
              <font-awesome-icon :icon="faArrowUpRightFromSquare"/>
            </Button>
            <Button
              :data-pendo="PendoFeature.Settings.ShortcutLokus"
              :href="oldContentUrl"
              class="justify-between"
              size="lg"
            >
              {{ t('settings.shortcuts.oldContent') }}
              <font-awesome-icon :icon="faArrowUpRightFromSquare"/>
            </Button>
          </div>
        </div>
      </div>
    </template>
  </AuDrawer>
  <SettingsGroups
    :open="groupsOpen"
    @back="toggleGroups()"
    @close="emit('close')"
  />
  <SettingsSubscription
    :open="subscriptionsOpen"
    @back="toggleSubscriptions()"
    @close="emit('close')"
  />
</template>
